import {
  FocMemberApplyPayload,
  FocProfileApplyResponseData,
  FocProfileUpdatePayload,
  KolProfile,
  KolSocialLink,
} from '@/types/campaign'

// Tự nhận diện nền tảng từ URL/text người dùng nhập (vd: link thật hoặc chỉ
// gõ tên kiểu "... on TikTok") để tách platform khi gửi lên BE.
const PLATFORM_PATTERNS: readonly { platform: string; pattern: RegExp }[] = [
  { platform: 'tiktok', pattern: /tiktok/i },
  { platform: 'facebook', pattern: /facebook|fb\.com/i },
  { platform: 'instagram', pattern: /instagram/i },
  { platform: 'youtube', pattern: /youtube|youtu\.be/i },
  { platform: 'twitter', pattern: /twitter|(?:^|[./])x\.com/i },
  { platform: 'threads', pattern: /threads\.net/i },
  { platform: 'zalo', pattern: /zalo/i },
]

export const detectSocialPlatform = (link: string): string =>
  PLATFORM_PATTERNS.find(({ pattern }) => pattern.test(link))?.platform ??
  'other'

export const buildSocialLinksPayload = (
  links: readonly string[]
): KolSocialLink[] =>
  links.map((url) => ({ platform: detectSocialPlatform(url), url }))

// Rút gọn link để hiển thị gọn như design (bỏ https://, www.) - text tự do
// (vd "... on TikTok") không match được thì giữ nguyên.
export const formatSocialLinkDisplay = (link: string): string =>
  link.replace(/^https?:\/\//i, '').replace(/^www\./i, '')

const parseSports = (sports: string): string[] =>
  sports
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

export const buildFocMemberApplyPayload = (
  profile: KolProfile
): FocMemberApplyPayload => ({
  // Không bắt buộc chọn giới tính - chỉ gửi khi đã có giá trị.
  ...(profile.gender_type && { gender_type: profile.gender_type }),
  fullname: profile.fullName,
  phone: profile.phone,
  email: profile.email,
  address: profile.address,
  city: profile.city,
  district: profile.district,
  ward: profile.ward,
  social_links: buildSocialLinksPayload(profile.socialLinks),
  sports: parseSports(profile.sports),
  height: Number(profile.height) || 0,
  weight: Number(profile.weight) || 0,
})

// Chỉ gửi field nào thực sự thay đổi (dirtyFields của react-hook-form) lên
// POST /foc-profiles/me, tránh ghi đè các field khác chưa được sửa.
export const buildFocProfileUpdatePayload = (
  profile: KolProfile,
  dirty: Partial<Record<keyof KolProfile, unknown>>
): FocProfileUpdatePayload => {
  return {
    ...(!!dirty.gender_type &&
      profile.gender_type && { gender_type: profile.gender_type }),
    ...(!!dirty.fullName && { fullname: profile.fullName }),
    ...(!!dirty.phone && { phone: profile.phone }),
    ...(!!dirty.email && { email: profile.email }),
    ...(!!dirty.address && { address: profile.address }),
    ...(!!dirty.city && { city: profile.city }),
    ...(!!dirty.district && { district: profile.district }),
    ...(!!dirty.ward && { ward: profile.ward }),
    ...(!!dirty.socialLinks && {
      social_links: buildSocialLinksPayload(profile.socialLinks),
    }),
    ...(!!dirty.sports && { sports: parseSports(profile.sports) }),
    ...(!!dirty.height && { height: Number(profile.height) || 0 }),
    ...(!!dirty.weight && { weight: Number(profile.weight) || 0 }),
  }
}

// Response trả về đủ mọi field (kể cả field không đổi) nên map lại y hệt
// mapFocProfileResponseToKolProfile cho cả luồng apply và update.
export const mapFocProfileResponseToKolProfile = (
  data: FocProfileApplyResponseData
): KolProfile => ({
  gender_type: data.gender_type ?? '',
  fullName: data.fullname,
  phone: data.phone,
  email: data.email,
  socialLinks: data.social_links.map((link) => link.url),
  sports: data.sports.join(', '),
  height: data.height ? String(data.height) : '',
  weight: data.weight ? String(data.weight) : '',
  address: data.address,
  city: data.city,
  district: data.district,
  ward: data.ward,
  status: data.status,
})
